import * as Tabs from "@radix-ui/react-tabs";
import classNames from "classnames";
import { ReactNode } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";
import { BlurFade } from "components/design/BlurFade";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { Header } from "components/DesignSystem/Header/Header";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import DashboardContainer from "components/dashboard/DashboardContainer";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useFilters } from "hooks/useFilter";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { MultiValue, SingleValue } from "react-select";
import { getSeasonsFrom } from "utils/getAllSeason";
import { useGetEstimatedTaxWaterfallQuery } from "store/apis/estimatedTax";
import { EstimatedTaxEmptyState } from "./EstimatedTaxEmptyState";
import { EstimatedTaxOverview } from "./EstimatedTaxOverview";
import { EstimatedTaxBreakdown } from "./EstimatedTaxBreakdown";
import { EstimatedTaxSettingsPanel } from "./EstimatedTaxSettingsPanel";
import { TaxBenefits } from "./TaxBenefits/TaxBenefits";

type Props = {
  title: ReactNode;
  containerClassName?: string;
  tabNav?: ReactNode;
};

export const EstimatedTaxDashboardBody = ({
  title,
  containerClassName,
  tabNav,
}: Props) => {
  const currentYear = dayjs().year();
  const allSeasons = getSeasonsFrom(2021, currentYear + 1);
  const entityId = useCurrentEntityId();
  const { isAdmin, isCpa } = useRoleBasedView();
  const isServiceUser = isAdmin || isCpa;

  const { url } = useRouteMatch();
  const { pathname } = useLocation();
  const isSettingsTab = pathname === `${url}/settings`;
  const isTaxBenefitsTab = pathname === `${url}/tax-benefits`;

  const { values, updateFilter } = useFilters({
    initialValue: { season: currentYear.toString() },
    useQueryParams: true,
  });
  const { season } = values;

  const { data, isLoading, isFetching, isSuccess, error } =
    useGetEstimatedTaxWaterfallQuery(
      { entityId: entityId!, year: season! },
      { skip: !season || !entityId }
    );

  const apiErrorMessage =
    error && "data" in error
      ? (error.data as { message?: string })?.message ?? null
      : null;

  const onSeasonChange = (
    option: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (option instanceof Array || !option?.value) return;
    updateFilter("season", option.value);
  };

  const isEnrolled = data?.enrolled ?? false;

  return (
    <DashboardContainer className={classNames("t-h-full", containerClassName)}>
      <Tabs.Root value={pathname} className="t-flex t-flex-col t-h-full">
        <DashboardContainer.Header className="t-z-header">
          <Header
            v2
            title={title}
            right={
              <div className="t-flex t-gap-2 t-items-center">
                <EntitySelector showOnlyUSEntities size="small" />
                <Combobox
                  menuPortalTarget={document.body}
                  size="small"
                  className="t-w-24"
                  classNames={{
                    control: () => "!t-justify-between",
                  }}
                  isSearchable={false}
                  isClearable={false}
                  onChange={onSeasonChange}
                  value={season ? { value: season, label: season } : null}
                  options={allSeasons.map((s) => ({
                    value: String(s),
                    label: String(s),
                  }))}
                />
              </div>
            }
            bottom={tabNav}
          />
        </DashboardContainer.Header>

        <DashboardContainer.Content className="t-pb-10">
          {isSettingsTab && (
            <Tabs.Content value={`${url}/settings`}>
              {entityId && <EstimatedTaxSettingsPanel entityId={entityId} />}
            </Tabs.Content>
          )}
          {isTaxBenefitsTab && (
            <Tabs.Content value={`${url}/tax-benefits`}>
              {entityId && (
                <TaxBenefits
                  entityId={entityId}
                  year={season ?? String(currentYear)}
                />
              )}
            </Tabs.Content>
          )}
          {!isSettingsTab && !isTaxBenefitsTab && (
            <>
              {!isSuccess && apiErrorMessage && (
                <Banner type="error" description={apiErrorMessage} />
              )}
              <Async.Root
                isLoading={isLoading || isFetching}
                isSuccess={isSuccess && !isFetching}
                isEmpty={!isEnrolled}
                renderOnLoad={
                  <div className="t-flex t-flex-col t-mt-3 t-gap-5 t-pb-20">
                    <Skeleton className="t-min-h-40" />
                    <Skeleton className="t-min-h-64" />
                  </div>
                }
              >
                <Async.Empty>
                  <EstimatedTaxEmptyState entityId={entityId!} />
                </Async.Empty>
                <Async.Success>
                  {data && (
                    <BlurFade
                      duration={0.2}
                      className="t-flex t-flex-col t-gap-5 t-pb-20"
                    >
                      <Tabs.Content
                        value={url}
                        className="t-flex t-flex-col t-gap-4 t-mt-5"
                      >
                        {data.data_source === "NO_DATA" && !isServiceUser && (
                          <Banner
                            type="neutral"
                            description={`Your estimated tax for ${season} is being prepared. Check back once your bookkeeper closes your first month.`}
                          />
                        )}
                        {data.data_source === "MONTHLY_BOOKS" && (
                          <Banner
                            type="neutral"
                            description={`Calculated from ${
                              data.months_closed
                            } closed month${
                              data.months_closed !== 1 ? "s" : ""
                            } of bookkeeping data, annualised to a full year.`}
                          />
                        )}
                        {data.data_source === "MANUAL" && (
                          <Banner
                            type="neutral"
                            description="Calculated from manually entered figures. These will be replaced automatically once bookkeeping months are closed."
                          />
                        )}
                        {data.waterfall && (
                          <EstimatedTaxOverview
                            waterfall={data.waterfall}
                            quarters={data.quarterly_breakdown}
                            payments={data.payments}
                            entityId={entityId!}
                            year={season!}
                            settings={data.settings}
                          />
                        )}
                      </Tabs.Content>

                      <Tabs.Content value={`${url}/breakdown`}>
                        <EstimatedTaxBreakdown
                          months={data.monthly_breakdown ?? []}
                          quarters={data.quarterly_breakdown}
                          entityId={entityId!}
                          year={season!}
                          isAdmin={isServiceUser}
                          syncConfig={data.sync_config ?? null}
                          adjustments={data.adjustments ?? []}
                          isFetching={isFetching}
                          projectionSource={data.projection_source ?? null}
                          waterfall={data.waterfall}
                        />
                      </Tabs.Content>
                    </BlurFade>
                  )}
                </Async.Success>
              </Async.Root>
            </>
          )}
        </DashboardContainer.Content>
      </Tabs.Root>
    </DashboardContainer>
  );
};
